/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const U = globalThis, J = U.ShadowRoot && (U.ShadyCSS === void 0 || U.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, Q = Symbol(), te = /* @__PURE__ */ new WeakMap();
let me = class {
  constructor(e, t, i) {
    if (this._$cssResult$ = !0, i !== Q) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = t;
  }
  get styleSheet() {
    let e = this.o;
    const t = this.t;
    if (J && e === void 0) {
      const i = t !== void 0 && t.length === 1;
      i && (e = te.get(t)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), i && te.set(t, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const ge = (n) => new me(typeof n == "string" ? n : n + "", void 0, Q), ke = (n, ...e) => {
  const t = n.length === 1 ? n[0] : e.reduce((i, r, o) => i + ((s) => {
    if (s._$cssResult$ === !0) return s.cssText;
    if (typeof s == "number") return s;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + s + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(r) + n[o + 1], n[0]);
  return new me(t, n, Q);
}, Se = (n, e) => {
  if (J) n.adoptedStyleSheets = e.map((t) => t instanceof CSSStyleSheet ? t : t.styleSheet);
  else for (const t of e) {
    const i = document.createElement("style"), r = U.litNonce;
    r !== void 0 && i.setAttribute("nonce", r), i.textContent = t.cssText, n.appendChild(i);
  }
}, ie = J ? (n) => n : (n) => n instanceof CSSStyleSheet ? ((e) => {
  let t = "";
  for (const i of e.cssRules) t += i.cssText;
  return ge(t);
})(n) : n;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Ce, defineProperty: He, getOwnPropertyDescriptor: ze, getOwnPropertyNames: Be, getOwnPropertySymbols: Pe, getPrototypeOf: Re } = Object, w = globalThis, ne = w.trustedTypes, Me = ne ? ne.emptyScript : "", W = w.reactiveElementPolyfillSupport, C = (n, e) => n, N = { toAttribute(n, e) {
  switch (e) {
    case Boolean:
      n = n ? Me : null;
      break;
    case Object:
    case Array:
      n = n == null ? n : JSON.stringify(n);
  }
  return n;
}, fromAttribute(n, e) {
  let t = n;
  switch (e) {
    case Boolean:
      t = n !== null;
      break;
    case Number:
      t = n === null ? null : Number(n);
      break;
    case Object:
    case Array:
      try {
        t = JSON.parse(n);
      } catch {
        t = null;
      }
  }
  return t;
} }, X = (n, e) => !Ce(n, e), re = { attribute: !0, type: String, converter: N, reflect: !1, hasChanged: X };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), w.litPropertyMetadata ?? (w.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class _ extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, t = re) {
    if (t.state && (t.attribute = !1), this._$Ei(), this.elementProperties.set(e, t), !t.noAccessor) {
      const i = Symbol(), r = this.getPropertyDescriptor(e, i, t);
      r !== void 0 && He(this.prototype, e, r);
    }
  }
  static getPropertyDescriptor(e, t, i) {
    const { get: r, set: o } = ze(this.prototype, e) ?? { get() {
      return this[t];
    }, set(s) {
      this[t] = s;
    } };
    return { get() {
      return r == null ? void 0 : r.call(this);
    }, set(s) {
      const l = r == null ? void 0 : r.call(this);
      o.call(this, s), this.requestUpdate(e, l, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? re;
  }
  static _$Ei() {
    if (this.hasOwnProperty(C("elementProperties"))) return;
    const e = Re(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(C("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(C("properties"))) {
      const t = this.properties, i = [...Be(t), ...Pe(t)];
      for (const r of i) this.createProperty(r, t[r]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const t = litPropertyMetadata.get(e);
      if (t !== void 0) for (const [i, r] of t) this.elementProperties.set(i, r);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [t, i] of this.elementProperties) {
      const r = this._$Eu(t, i);
      r !== void 0 && this._$Eh.set(r, t);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const t = [];
    if (Array.isArray(e)) {
      const i = new Set(e.flat(1 / 0).reverse());
      for (const r of i) t.unshift(ie(r));
    } else e !== void 0 && t.push(ie(e));
    return t;
  }
  static _$Eu(e, t) {
    const i = t.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((t) => t(this));
  }
  addController(e) {
    var t;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((t = e.hostConnected) == null || t.call(e));
  }
  removeController(e) {
    var t;
    (t = this._$EO) == null || t.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), t = this.constructor.elementProperties;
    for (const i of t.keys()) this.hasOwnProperty(i) && (e.set(i, this[i]), delete this[i]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return Se(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostConnected) == null ? void 0 : i.call(t);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostDisconnected) == null ? void 0 : i.call(t);
    });
  }
  attributeChangedCallback(e, t, i) {
    this._$AK(e, i);
  }
  _$EC(e, t) {
    var o;
    const i = this.constructor.elementProperties.get(e), r = this.constructor._$Eu(e, i);
    if (r !== void 0 && i.reflect === !0) {
      const s = (((o = i.converter) == null ? void 0 : o.toAttribute) !== void 0 ? i.converter : N).toAttribute(t, i.type);
      this._$Em = e, s == null ? this.removeAttribute(r) : this.setAttribute(r, s), this._$Em = null;
    }
  }
  _$AK(e, t) {
    var o;
    const i = this.constructor, r = i._$Eh.get(e);
    if (r !== void 0 && this._$Em !== r) {
      const s = i.getPropertyOptions(r), l = typeof s.converter == "function" ? { fromAttribute: s.converter } : ((o = s.converter) == null ? void 0 : o.fromAttribute) !== void 0 ? s.converter : N;
      this._$Em = r, this[r] = l.fromAttribute(t, s.type), this._$Em = null;
    }
  }
  requestUpdate(e, t, i) {
    if (e !== void 0) {
      if (i ?? (i = this.constructor.getPropertyOptions(e)), !(i.hasChanged ?? X)(this[e], t)) return;
      this.P(e, t, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, t, i) {
    this._$AL.has(e) || this._$AL.set(e, t), i.reflect === !0 && this._$Em !== e && (this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Set())).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (t) {
      Promise.reject(t);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var i;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [o, s] of this._$Ep) this[o] = s;
        this._$Ep = void 0;
      }
      const r = this.constructor.elementProperties;
      if (r.size > 0) for (const [o, s] of r) s.wrapped !== !0 || this._$AL.has(o) || this[o] === void 0 || this.P(o, this[o], s);
    }
    let e = !1;
    const t = this._$AL;
    try {
      e = this.shouldUpdate(t), e ? (this.willUpdate(t), (i = this._$EO) == null || i.forEach((r) => {
        var o;
        return (o = r.hostUpdate) == null ? void 0 : o.call(r);
      }), this.update(t)) : this._$EU();
    } catch (r) {
      throw e = !1, this._$EU(), r;
    }
    e && this._$AE(t);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var t;
    (t = this._$EO) == null || t.forEach((i) => {
      var r;
      return (r = i.hostUpdated) == null ? void 0 : r.call(i);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((t) => this._$EC(t, this[t]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
}
_.elementStyles = [], _.shadowRootOptions = { mode: "open" }, _[C("elementProperties")] = /* @__PURE__ */ new Map(), _[C("finalized")] = /* @__PURE__ */ new Map(), W == null || W({ ReactiveElement: _ }), (w.reactiveElementVersions ?? (w.reactiveElementVersions = [])).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const H = globalThis, L = H.trustedTypes, oe = L ? L.createPolicy("lit-html", { createHTML: (n) => n }) : void 0, we = "$lit$", g = `lit$${Math.random().toFixed(9).slice(2)}$`, be = "?" + g, Oe = `<${be}>`, x = document, P = () => x.createComment(""), R = (n) => n === null || typeof n != "object" && typeof n != "function", Z = Array.isArray, Te = (n) => Z(n) || typeof (n == null ? void 0 : n[Symbol.iterator]) == "function", Y = `[ 	
\f\r]`, E = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, se = /-->/g, ae = />/g, b = RegExp(`>|${Y}(?:([^\\s"'>=/]+)(${Y}*=${Y}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), le = /'/g, de = /"/g, ye = /^(?:script|style|textarea|title)$/i, Ue = (n) => (e, ...t) => ({ _$litType$: n, strings: e, values: t }), he = Ue(1), v = Symbol.for("lit-noChange"), h = Symbol.for("lit-nothing"), ce = /* @__PURE__ */ new WeakMap(), y = x.createTreeWalker(x, 129);
function xe(n, e) {
  if (!Z(n) || !n.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return oe !== void 0 ? oe.createHTML(e) : e;
}
const Ne = (n, e) => {
  const t = n.length - 1, i = [];
  let r, o = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", s = E;
  for (let l = 0; l < t; l++) {
    const a = n[l];
    let c, u, d = -1, p = 0;
    for (; p < a.length && (s.lastIndex = p, u = s.exec(a), u !== null); ) p = s.lastIndex, s === E ? u[1] === "!--" ? s = se : u[1] !== void 0 ? s = ae : u[2] !== void 0 ? (ye.test(u[2]) && (r = RegExp("</" + u[2], "g")), s = b) : u[3] !== void 0 && (s = b) : s === b ? u[0] === ">" ? (s = r ?? E, d = -1) : u[1] === void 0 ? d = -2 : (d = s.lastIndex - u[2].length, c = u[1], s = u[3] === void 0 ? b : u[3] === '"' ? de : le) : s === de || s === le ? s = b : s === se || s === ae ? s = E : (s = b, r = void 0);
    const m = s === b && n[l + 1].startsWith("/>") ? " " : "";
    o += s === E ? a + Oe : d >= 0 ? (i.push(c), a.slice(0, d) + we + a.slice(d) + g + m) : a + g + (d === -2 ? l : m);
  }
  return [xe(n, o + (n[t] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), i];
};
class M {
  constructor({ strings: e, _$litType$: t }, i) {
    let r;
    this.parts = [];
    let o = 0, s = 0;
    const l = e.length - 1, a = this.parts, [c, u] = Ne(e, t);
    if (this.el = M.createElement(c, i), y.currentNode = this.el.content, t === 2 || t === 3) {
      const d = this.el.content.firstChild;
      d.replaceWith(...d.childNodes);
    }
    for (; (r = y.nextNode()) !== null && a.length < l; ) {
      if (r.nodeType === 1) {
        if (r.hasAttributes()) for (const d of r.getAttributeNames()) if (d.endsWith(we)) {
          const p = u[s++], m = r.getAttribute(d).split(g), T = /([.?@])?(.*)/.exec(p);
          a.push({ type: 1, index: o, name: T[2], strings: m, ctor: T[1] === "." ? je : T[1] === "?" ? De : T[1] === "@" ? Ie : D }), r.removeAttribute(d);
        } else d.startsWith(g) && (a.push({ type: 6, index: o }), r.removeAttribute(d));
        if (ye.test(r.tagName)) {
          const d = r.textContent.split(g), p = d.length - 1;
          if (p > 0) {
            r.textContent = L ? L.emptyScript : "";
            for (let m = 0; m < p; m++) r.append(d[m], P()), y.nextNode(), a.push({ type: 2, index: ++o });
            r.append(d[p], P());
          }
        }
      } else if (r.nodeType === 8) if (r.data === be) a.push({ type: 2, index: o });
      else {
        let d = -1;
        for (; (d = r.data.indexOf(g, d + 1)) !== -1; ) a.push({ type: 7, index: o }), d += g.length - 1;
      }
      o++;
    }
  }
  static createElement(e, t) {
    const i = x.createElement("template");
    return i.innerHTML = e, i;
  }
}
function A(n, e, t = n, i) {
  var s, l;
  if (e === v) return e;
  let r = i !== void 0 ? (s = t._$Co) == null ? void 0 : s[i] : t._$Cl;
  const o = R(e) ? void 0 : e._$litDirective$;
  return (r == null ? void 0 : r.constructor) !== o && ((l = r == null ? void 0 : r._$AO) == null || l.call(r, !1), o === void 0 ? r = void 0 : (r = new o(n), r._$AT(n, t, i)), i !== void 0 ? (t._$Co ?? (t._$Co = []))[i] = r : t._$Cl = r), r !== void 0 && (e = A(n, r._$AS(n, e.values), r, i)), e;
}
class Le {
  constructor(e, t) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = t;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: t }, parts: i } = this._$AD, r = ((e == null ? void 0 : e.creationScope) ?? x).importNode(t, !0);
    y.currentNode = r;
    let o = y.nextNode(), s = 0, l = 0, a = i[0];
    for (; a !== void 0; ) {
      if (s === a.index) {
        let c;
        a.type === 2 ? c = new O(o, o.nextSibling, this, e) : a.type === 1 ? c = new a.ctor(o, a.name, a.strings, this, e) : a.type === 6 && (c = new We(o, this, e)), this._$AV.push(c), a = i[++l];
      }
      s !== (a == null ? void 0 : a.index) && (o = y.nextNode(), s++);
    }
    return y.currentNode = x, r;
  }
  p(e) {
    let t = 0;
    for (const i of this._$AV) i !== void 0 && (i.strings !== void 0 ? (i._$AI(e, i, t), t += i.strings.length - 2) : i._$AI(e[t])), t++;
  }
}
class O {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this._$Cv;
  }
  constructor(e, t, i, r) {
    this.type = 2, this._$AH = h, this._$AN = void 0, this._$AA = e, this._$AB = t, this._$AM = i, this.options = r, this._$Cv = (r == null ? void 0 : r.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const t = this._$AM;
    return t !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = t.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, t = this) {
    e = A(this, e, t), R(e) ? e === h || e == null || e === "" ? (this._$AH !== h && this._$AR(), this._$AH = h) : e !== this._$AH && e !== v && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Te(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== h && R(this._$AH) ? this._$AA.nextSibling.data = e : this.T(x.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var o;
    const { values: t, _$litType$: i } = e, r = typeof i == "number" ? this._$AC(e) : (i.el === void 0 && (i.el = M.createElement(xe(i.h, i.h[0]), this.options)), i);
    if (((o = this._$AH) == null ? void 0 : o._$AD) === r) this._$AH.p(t);
    else {
      const s = new Le(r, this), l = s.u(this.options);
      s.p(t), this.T(l), this._$AH = s;
    }
  }
  _$AC(e) {
    let t = ce.get(e.strings);
    return t === void 0 && ce.set(e.strings, t = new M(e)), t;
  }
  k(e) {
    Z(this._$AH) || (this._$AH = [], this._$AR());
    const t = this._$AH;
    let i, r = 0;
    for (const o of e) r === t.length ? t.push(i = new O(this.O(P()), this.O(P()), this, this.options)) : i = t[r], i._$AI(o), r++;
    r < t.length && (this._$AR(i && i._$AB.nextSibling, r), t.length = r);
  }
  _$AR(e = this._$AA.nextSibling, t) {
    var i;
    for ((i = this._$AP) == null ? void 0 : i.call(this, !1, !0, t); e && e !== this._$AB; ) {
      const r = e.nextSibling;
      e.remove(), e = r;
    }
  }
  setConnected(e) {
    var t;
    this._$AM === void 0 && (this._$Cv = e, (t = this._$AP) == null || t.call(this, e));
  }
}
class D {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, t, i, r, o) {
    this.type = 1, this._$AH = h, this._$AN = void 0, this.element = e, this.name = t, this._$AM = r, this.options = o, i.length > 2 || i[0] !== "" || i[1] !== "" ? (this._$AH = Array(i.length - 1).fill(new String()), this.strings = i) : this._$AH = h;
  }
  _$AI(e, t = this, i, r) {
    const o = this.strings;
    let s = !1;
    if (o === void 0) e = A(this, e, t, 0), s = !R(e) || e !== this._$AH && e !== v, s && (this._$AH = e);
    else {
      const l = e;
      let a, c;
      for (e = o[0], a = 0; a < o.length - 1; a++) c = A(this, l[i + a], t, a), c === v && (c = this._$AH[a]), s || (s = !R(c) || c !== this._$AH[a]), c === h ? e = h : e !== h && (e += (c ?? "") + o[a + 1]), this._$AH[a] = c;
    }
    s && !r && this.j(e);
  }
  j(e) {
    e === h ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class je extends D {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === h ? void 0 : e;
  }
}
class De extends D {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== h);
  }
}
class Ie extends D {
  constructor(e, t, i, r, o) {
    super(e, t, i, r, o), this.type = 5;
  }
  _$AI(e, t = this) {
    if ((e = A(this, e, t, 0) ?? h) === v) return;
    const i = this._$AH, r = e === h && i !== h || e.capture !== i.capture || e.once !== i.once || e.passive !== i.passive, o = e !== h && (i === h || r);
    r && this.element.removeEventListener(this.name, this, i), o && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var t;
    typeof this._$AH == "function" ? this._$AH.call(((t = this.options) == null ? void 0 : t.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class We {
  constructor(e, t, i) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = t, this.options = i;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    A(this, e);
  }
}
const V = H.litHtmlPolyfillSupport;
V == null || V(M, O), (H.litHtmlVersions ?? (H.litHtmlVersions = [])).push("3.2.1");
const Ye = (n, e, t) => {
  const i = (t == null ? void 0 : t.renderBefore) ?? e;
  let r = i._$litPart$;
  if (r === void 0) {
    const o = (t == null ? void 0 : t.renderBefore) ?? null;
    i._$litPart$ = r = new O(e.insertBefore(P(), o), o, void 0, t ?? {});
  }
  return r._$AI(n), r;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let z = class extends _ {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var t;
    const e = super.createRenderRoot();
    return (t = this.renderOptions).renderBefore ?? (t.renderBefore = e.firstChild), e;
  }
  update(e) {
    const t = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = Ye(t, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return v;
  }
};
var fe;
z._$litElement$ = !0, z.finalized = !0, (fe = globalThis.litElementHydrateSupport) == null || fe.call(globalThis, { LitElement: z });
const F = globalThis.litElementPolyfillSupport;
F == null || F({ LitElement: z });
(globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ve = { attribute: !0, type: String, converter: N, reflect: !1, hasChanged: X }, Fe = (n = Ve, e, t) => {
  const { kind: i, metadata: r } = t;
  let o = globalThis.litPropertyMetadata.get(r);
  if (o === void 0 && globalThis.litPropertyMetadata.set(r, o = /* @__PURE__ */ new Map()), o.set(t.name, n), i === "accessor") {
    const { name: s } = t;
    return { set(l) {
      const a = e.get.call(this);
      e.set.call(this, l), this.requestUpdate(s, a, n);
    }, init(l) {
      return l !== void 0 && this.P(s, void 0, n), l;
    } };
  }
  if (i === "setter") {
    const { name: s } = t;
    return function(l) {
      const a = this[s];
      e.call(this, l), this.requestUpdate(s, a, n);
    };
  }
  throw Error("Unsupported decorator location: " + i);
};
function I(n) {
  return (e, t) => typeof t == "object" ? Fe(n, e, t) : ((i, r, o) => {
    const s = r.hasOwnProperty(o);
    return r.constructor.createProperty(o, s ? { ...i, wrapped: !0 } : i), s ? Object.getOwnPropertyDescriptor(r, o) : void 0;
  })(n, e, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function qe(n) {
  return I({ ...n, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ke = (n, e, t) => (t.configurable = !0, t.enumerable = !0, Reflect.decorate && typeof e != "object" && Object.defineProperty(n, e, t), t);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function G(n, e) {
  return (t, i, r) => {
    const o = (s) => {
      var l;
      return ((l = s.renderRoot) == null ? void 0 : l.querySelector(n)) ?? null;
    };
    return Ke(t, i, { get() {
      return o(this);
    } });
  };
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Je = (n) => n.strings === void 0;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ve = { ATTRIBUTE: 1, CHILD: 2 }, $e = (n) => (...e) => ({ _$litDirective$: n, values: e });
class _e {
  constructor(e) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(e, t, i) {
    this._$Ct = e, this._$AM = t, this._$Ci = i;
  }
  _$AS(e, t) {
    return this.update(e, t);
  }
  update(e, t) {
    return this.render(...t);
  }
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const B = (n, e) => {
  var i;
  const t = n._$AN;
  if (t === void 0) return !1;
  for (const r of t) (i = r._$AO) == null || i.call(r, e, !1), B(r, e);
  return !0;
}, j = (n) => {
  let e, t;
  do {
    if ((e = n._$AM) === void 0) break;
    t = e._$AN, t.delete(n), n = e;
  } while ((t == null ? void 0 : t.size) === 0);
}, Ae = (n) => {
  for (let e; e = n._$AM; n = e) {
    let t = e._$AN;
    if (t === void 0) e._$AN = t = /* @__PURE__ */ new Set();
    else if (t.has(n)) break;
    t.add(n), Ze(e);
  }
};
function Qe(n) {
  this._$AN !== void 0 ? (j(this), this._$AM = n, Ae(this)) : this._$AM = n;
}
function Xe(n, e = !1, t = 0) {
  const i = this._$AH, r = this._$AN;
  if (r !== void 0 && r.size !== 0) if (e) if (Array.isArray(i)) for (let o = t; o < i.length; o++) B(i[o], !1), j(i[o]);
  else i != null && (B(i, !1), j(i));
  else B(this, n);
}
const Ze = (n) => {
  n.type == ve.CHILD && (n._$AP ?? (n._$AP = Xe), n._$AQ ?? (n._$AQ = Qe));
};
class Ge extends _e {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(e, t, i) {
    super._$AT(e, t, i), Ae(this), this.isConnected = e._$AU;
  }
  _$AO(e, t = !0) {
    var i, r;
    e !== this.isConnected && (this.isConnected = e, e ? (i = this.reconnected) == null || i.call(this) : (r = this.disconnected) == null || r.call(this)), t && (B(this, e), j(this));
  }
  setValue(e) {
    if (Je(this._$Ct)) this._$Ct._$AI(e, this);
    else {
      const t = [...this._$Ct._$AH];
      t[this._$Ci] = e, this._$Ct._$AI(t, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const k = () => new et();
class et {
}
const q = /* @__PURE__ */ new WeakMap(), S = $e(class extends Ge {
  render(n) {
    return h;
  }
  update(n, [e]) {
    var i;
    const t = e !== this.Y;
    return t && this.Y !== void 0 && this.rt(void 0), (t || this.lt !== this.ct) && (this.Y = e, this.ht = (i = n.options) == null ? void 0 : i.host, this.rt(this.ct = n.element)), h;
  }
  rt(n) {
    if (this.isConnected || (n = void 0), typeof this.Y == "function") {
      const e = this.ht ?? globalThis;
      let t = q.get(e);
      t === void 0 && (t = /* @__PURE__ */ new WeakMap(), q.set(e, t)), t.get(this.Y) !== void 0 && this.Y.call(this.ht, void 0), t.set(this.Y, n), n !== void 0 && this.Y.call(this.ht, n);
    } else this.Y.value = n;
  }
  get lt() {
    var n, e;
    return typeof this.Y == "function" ? (n = q.get(this.ht ?? globalThis)) == null ? void 0 : n.get(this.Y) : (e = this.Y) == null ? void 0 : e.value;
  }
  disconnected() {
    this.lt === this.ct && this.rt(void 0);
  }
  reconnected() {
    this.rt(this.ct);
  }
});
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ue = $e(class extends _e {
  constructor(n) {
    var e;
    if (super(n), n.type !== ve.ATTRIBUTE || n.name !== "class" || ((e = n.strings) == null ? void 0 : e.length) > 2) throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(n) {
    return " " + Object.keys(n).filter((e) => n[e]).join(" ") + " ";
  }
  update(n, [e]) {
    var i, r;
    if (this.st === void 0) {
      this.st = /* @__PURE__ */ new Set(), n.strings !== void 0 && (this.nt = new Set(n.strings.join(" ").split(/\s/).filter((o) => o !== "")));
      for (const o in e) e[o] && !((i = this.nt) != null && i.has(o)) && this.st.add(o);
      return this.render(e);
    }
    const t = n.element.classList;
    for (const o of this.st) o in e || (t.remove(o), this.st.delete(o));
    for (const o in e) {
      const s = !!e[o];
      s === this.st.has(o) || (r = this.nt) != null && r.has(o) || (s ? (t.add(o), this.st.add(o)) : (t.remove(o), this.st.delete(o)));
    }
    return v;
  }
}), tt = 'html{font-size:6px}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:500;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Medium.woff2) format("woff2")}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:400;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Regular.woff2) format("woff2")}html,:root,:host{--nui-alabaster: rgba(250,248,247,1);--nui-backdrop-layer: rgba(26,26,26,.5);--nui-beyond-bronze: rgba(167,71,18,1);--nui-beyond-bronze-bg: rgba(247,238,232,1);--nui-breuninger-orange: rgba(243,138,1,1);--nui-breuninger-orange-bg: rgba(255,244,240,1);--nui-breuninger-red: rgba(186,12,47,1);--nui-breuninger-red-hover: rgba(186,12,47,1);--nui-business: rgba(108,103,101,1);--nui-coal: rgba(26,26,26,1);--nui-concrete: rgba(244,242,242,1);--nui-error: rgba(213,0,0,1);--nui-error-bg: rgba(255,240,240,1);--nui-ghost: rgba(255,255,255,0);--nui-home-and-living: rgba(186,180,180,1);--nui-marketplace: rgba(16,53,185,1);--nui-red-button-hover: rgba(140,9,36,1);--nui-sale: rgba(255,237,0,1);--nui-smoke: rgba(146,141,139,1);--nui-snow: rgba(255,255,255,1);--nui-success: rgba(65,117,5,1);--nui-success-bg: rgba(238,248,228,1);--nui-sustainability: rgba(224,218,210,1);--nui-space-1: 1rem;--nui-space-10: 10rem;--nui-space-11: 11rem;--nui-space-12: 12rem;--nui-space-13: 13rem;--nui-space-14: 14rem;--nui-space-15: 15rem;--nui-space-16: 16rem;--nui-space-17: 17rem;--nui-space-2: 2rem;--nui-space-2xs: 1rem;--nui-space-3: 3rem;--nui-space-4: 4rem;--nui-space-5: 5rem;--nui-space-6: 6rem;--nui-space-7: 7rem;--nui-space-8: 8rem;--nui-space-9: 9rem;--nui-space-ho-2xs: 1rem;--nui-space-ho-l: 5rem;--nui-space-ho-m: 4rem;--nui-space-ho-s: 3rem;--nui-space-ho-xl: 7rem;--nui-space-ho-xs: 2rem;--nui-space-l: 5rem;--nui-space-m: 4rem;--nui-space-s: 3rem;--nui-space-ve-2xl: 15rem;--nui-space-ve-2xs: 3rem;--nui-space-ve-3xl: 17rem;--nui-space-ve-3xs: 2rem;--nui-space-ve-4xs: 1rem;--nui-space-ve-l: 10rem;--nui-space-ve-m: 7rem;--nui-space-ve-s: 5rem;--nui-space-ve-xl: 12rem;--nui-space-ve-xs: 4rem;--nui-space-xl: 7rem;--nui-space-xs: 2rem;--nui-brand-heading-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-bold-font-size: 2.667rem;--nui-brand-heading-bold-font-weight: 500;--nui-brand-heading-bold-letter-spacing: .133rem;--nui-brand-heading-bold-line-height: 5rem;--nui-brand-heading-bold-text-decoration: none;--nui-brand-heading-bold-text-transform: inherit;--nui-brand-heading-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-font-size: 2.667rem;--nui-brand-heading-font-weight: 400;--nui-brand-heading-large-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-large-bold-font-size: 3.333rem;--nui-brand-heading-large-bold-font-weight: 500;--nui-brand-heading-large-bold-letter-spacing: .133rem;--nui-brand-heading-large-bold-line-height: 6rem;--nui-brand-heading-large-bold-text-decoration: none;--nui-brand-heading-large-bold-text-transform: inherit;--nui-brand-heading-large-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-large-font-size: 3.333rem;--nui-brand-heading-large-font-weight: 400;--nui-brand-heading-large-letter-spacing: .133rem;--nui-brand-heading-large-line-height: 6rem;--nui-brand-heading-large-text-decoration: none;--nui-brand-heading-large-text-transform: inherit;--nui-brand-heading-letter-spacing: .133rem;--nui-brand-heading-line-height: 5rem;--nui-brand-heading-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-small-font-size: 2.333rem;--nui-brand-heading-small-font-weight: 400;--nui-brand-heading-small-letter-spacing: .133rem;--nui-brand-heading-small-line-height: 3.667rem;--nui-brand-heading-small-text-decoration: none;--nui-brand-heading-small-text-transform: inherit;--nui-brand-heading-text-decoration: none;--nui-brand-heading-text-transform: inherit;--nui-button-copy-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-button-copy-font-size: 2.333rem;--nui-button-copy-font-weight: 500;--nui-button-copy-letter-spacing: .133rem;--nui-button-copy-line-height: 3.667rem;--nui-button-copy-text-decoration: none;--nui-button-copy-text-transform: uppercase;--nui-copy-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-bold-font-size: 2.667rem;--nui-copy-bold-font-weight: 500;--nui-copy-bold-letter-spacing: .133rem;--nui-copy-bold-line-height: 3.667rem;--nui-copy-bold-text-decoration: none;--nui-copy-bold-text-transform: inherit;--nui-copy-extra-small-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-extra-small-bold-font-size: 2rem;--nui-copy-extra-small-bold-font-weight: 500;--nui-copy-extra-small-bold-letter-spacing: .167rem;--nui-copy-extra-small-bold-line-height: 3.167rem;--nui-copy-extra-small-bold-text-decoration: none;--nui-copy-extra-small-bold-text-transform: inherit;--nui-copy-extra-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-extra-small-font-size: 2rem;--nui-copy-extra-small-font-weight: 400;--nui-copy-extra-small-letter-spacing: .167rem;--nui-copy-extra-small-line-height: 3.167rem;--nui-copy-extra-small-text-decoration: none;--nui-copy-extra-small-text-transform: inherit;--nui-copy-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-font-size: 2.667rem;--nui-copy-font-weight: 400;--nui-copy-large-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-large-bold-font-size: 4rem;--nui-copy-large-bold-font-weight: 500;--nui-copy-large-bold-letter-spacing: .133rem;--nui-copy-large-bold-line-height: 5rem;--nui-copy-large-bold-text-decoration: none;--nui-copy-large-bold-text-transform: inherit;--nui-copy-letter-spacing: .133rem;--nui-copy-line-height: 3.667rem;--nui-copy-medium-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-medium-bold-font-size: 3.333rem;--nui-copy-medium-bold-font-weight: 500;--nui-copy-medium-bold-letter-spacing: .133rem;--nui-copy-medium-bold-line-height: 4.333rem;--nui-copy-medium-bold-text-decoration: none;--nui-copy-medium-bold-text-transform: inherit;--nui-copy-medium-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-medium-font-size: 3.333rem;--nui-copy-medium-font-weight: 400;--nui-copy-medium-letter-spacing: .133rem;--nui-copy-medium-line-height: 4.333rem;--nui-copy-medium-text-decoration: none;--nui-copy-medium-text-transform: inherit;--nui-copy-small-bold-capitals-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-small-bold-capitals-font-size: 2.333rem;--nui-copy-small-bold-capitals-font-weight: 500;--nui-copy-small-bold-capitals-letter-spacing: .167rem;--nui-copy-small-bold-capitals-line-height: 3.167rem;--nui-copy-small-bold-capitals-text-decoration: none;--nui-copy-small-bold-capitals-text-transform: uppercase;--nui-copy-small-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-small-bold-font-size: 2.333rem;--nui-copy-small-bold-font-weight: 500;--nui-copy-small-bold-letter-spacing: .167rem;--nui-copy-small-bold-line-height: 3.167rem;--nui-copy-small-bold-text-decoration: none;--nui-copy-small-bold-text-transform: inherit;--nui-copy-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-small-font-size: 2.333rem;--nui-copy-small-font-weight: 400;--nui-copy-small-letter-spacing: .167rem;--nui-copy-small-line-height: 3.167rem;--nui-copy-small-text-decoration: none;--nui-copy-small-text-transform: inherit;--nui-copy-text-decoration: none;--nui-copy-text-transform: inherit;--nui-heading-extralarge-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-extralarge-font-size: 5rem;--nui-heading-extralarge-font-weight: 500;--nui-heading-extralarge-letter-spacing: .133rem;--nui-heading-extralarge-line-height: 6rem;--nui-heading-extralarge-text-decoration: none;--nui-heading-extralarge-text-transform: uppercase;--nui-heading-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-font-size: 2.667rem;--nui-heading-font-weight: 500;--nui-heading-large-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-large-font-size: 4rem;--nui-heading-large-font-weight: 500;--nui-heading-large-letter-spacing: .133rem;--nui-heading-large-line-height: 5rem;--nui-heading-large-text-decoration: none;--nui-heading-large-text-transform: uppercase;--nui-heading-letter-spacing: .133rem;--nui-heading-line-height: 3.667rem;--nui-heading-medium-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-medium-font-size: 3.333rem;--nui-heading-medium-font-weight: 500;--nui-heading-medium-letter-spacing: .133rem;--nui-heading-medium-line-height: 4.333rem;--nui-heading-medium-text-decoration: none;--nui-heading-medium-text-transform: uppercase;--nui-heading-text-decoration: none;--nui-heading-text-transform: uppercase;--nui-link-default-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-link-default-font-size: 2.667rem;--nui-link-default-font-weight: 400;--nui-link-default-letter-spacing: .133rem;--nui-link-default-line-height: 3.667rem;--nui-link-default-text-decoration: underline;--nui-link-default-text-transform: inherit;--nui-link-extra-small-default-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-link-extra-small-default-font-size: 2rem;--nui-link-extra-small-default-font-weight: 400;--nui-link-extra-small-default-letter-spacing: .167rem;--nui-link-extra-small-default-line-height: 3.167rem;--nui-link-extra-small-default-text-decoration: underline;--nui-link-extra-small-default-text-transform: inherit;--nui-link-small-default-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-link-small-default-font-size: 2.333rem;--nui-link-small-default-font-weight: 400;--nui-link-small-default-letter-spacing: .167rem;--nui-link-small-default-line-height: 3.167rem;--nui-link-small-default-text-decoration: underline;--nui-link-small-default-text-transform: inherit;--nui-prose-copy-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-bold-font-size: 2.667rem;--nui-prose-copy-bold-font-weight: 500;--nui-prose-copy-bold-letter-spacing: .133rem;--nui-prose-copy-bold-line-height: 5rem;--nui-prose-copy-bold-text-decoration: none;--nui-prose-copy-bold-text-transform: inherit;--nui-prose-copy-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-font-size: 2.667rem;--nui-prose-copy-font-weight: 400;--nui-prose-copy-letter-spacing: .133rem;--nui-prose-copy-line-height: 5rem;--nui-prose-copy-small-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-small-bold-font-size: 2.333rem;--nui-prose-copy-small-bold-font-weight: 500;--nui-prose-copy-small-bold-letter-spacing: .167rem;--nui-prose-copy-small-bold-line-height: 5rem;--nui-prose-copy-small-bold-text-decoration: none;--nui-prose-copy-small-bold-text-transform: inherit;--nui-prose-copy-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-small-font-size: 2.333rem;--nui-prose-copy-small-font-weight: 400;--nui-prose-copy-small-letter-spacing: .167rem;--nui-prose-copy-small-line-height: 4rem;--nui-prose-copy-small-text-decoration: none;--nui-prose-copy-small-text-transform: inherit;--nui-prose-copy-text-decoration: none;--nui-prose-copy-text-transform: inherit;--nui-prose-heading-extralarge-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-extralarge-font-size: 5rem;--nui-prose-heading-extralarge-font-weight: 500;--nui-prose-heading-extralarge-letter-spacing: .133rem;--nui-prose-heading-extralarge-line-height: 8rem;--nui-prose-heading-extralarge-text-decoration: none;--nui-prose-heading-extralarge-text-transform: uppercase;--nui-prose-heading-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-font-size: 3.333rem;--nui-prose-heading-font-weight: 400;--nui-prose-heading-large-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-large-font-size: 4rem;--nui-prose-heading-large-font-weight: 500;--nui-prose-heading-large-letter-spacing: .133rem;--nui-prose-heading-large-line-height: 7rem;--nui-prose-heading-large-text-decoration: none;--nui-prose-heading-large-text-transform: uppercase;--nui-prose-heading-letter-spacing: .133rem;--nui-prose-heading-line-height: 6rem;--nui-prose-heading-medium-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-medium-font-size: 3.333rem;--nui-prose-heading-medium-font-weight: 500;--nui-prose-heading-medium-letter-spacing: .133rem;--nui-prose-heading-medium-line-height: 6rem;--nui-prose-heading-medium-text-decoration: none;--nui-prose-heading-medium-text-transform: uppercase;--nui-prose-heading-text-decoration: none;--nui-prose-heading-text-transform: inherit;--nui-breakpoint-2xl: 1360px;--nui-breakpoint-2xs: 320px;--nui-breakpoint-3xl: 1660px;--nui-breakpoint-full-hd: 1920px;--nui-breakpoint-l: 960px;--nui-breakpoint-m: 760px;--nui-breakpoint-s: 560px;--nui-breakpoint-xl: 1160px;--nui-breakpoint-xs: 360px;--nui-elevation-above-header: 45;--nui-elevation-above-menu: 55;--nui-elevation-above-overlay: 85;--nui-elevation-below-header: 35;--nui-elevation-below-menu: 45;--nui-elevation-below-overlay: 55;--nui-elevation-header: 40;--nui-elevation-layer: 90;--nui-elevation-menu: 50;--nui-elevation-offcanvas: 100;--nui-elevation-overlay: 80}*,:before,:after{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }::backdrop{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }*,:before,:after{box-sizing:border-box;border-width:0;border-style:solid;border-color:#e5e7eb}:before,:after{--tw-content: ""}html,:host{line-height:1.5;-webkit-text-size-adjust:100%;-moz-tab-size:4;tab-size:4;font-family:Eduard Breuninger,ui-sans-serif,system-ui,sans-serif,"Apple Color Emoji","Segoe UI Emoji",Segoe UI Symbol,"Noto Color Emoji";font-feature-settings:normal;font-variation-settings:normal;-webkit-tap-highlight-color:transparent}body{margin:0;line-height:inherit}hr{height:0;color:inherit;border-top-width:1px}abbr:where([title]){text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:inherit}a{color:inherit;text-decoration:inherit}b,strong{font-weight:bolder}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-feature-settings:normal;font-variation-settings:normal;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-feature-settings:inherit;font-variation-settings:inherit;font-size:100%;font-weight:inherit;line-height:inherit;letter-spacing:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,input:where([type=button]),input:where([type=reset]),input:where([type=submit]){-webkit-appearance:button;background-color:transparent;background-image:none}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button,::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{list-style:none;margin:0;padding:0}dialog{padding:0}textarea{resize:vertical}input::placeholder,textarea::placeholder{opacity:1;color:#9ca3af}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{display:block;vertical-align:middle}img,video{max-width:100%;height:auto}[hidden]:where(:not([hidden=until-found])){display:none}.nui-tw-heading-medium{font-family:Eduard Breuninger;font-size:3.333rem;font-weight:500;line-height:4.333rem;letter-spacing:.133rem;text-transform:uppercase}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0}.static{position:static}.absolute{position:absolute}.relative{position:relative}.left-0{left:0}.right-0{right:0}.top-0{top:0}.z-10{z-index:10}.mb-1{margin-bottom:1rem}.inline{display:inline}.flex{display:flex}.h-\\[48px\\]{height:48px}.w-\\[48px\\]{width:48px}.w-full{width:100%}.transform{transform:translate(var(--tw-translate-x),var(--tw-translate-y)) rotate(var(--tw-rotate)) skew(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))}.resize{resize:both}.flex-row{flex-direction:row}.flex-nowrap{flex-wrap:nowrap}.items-center{align-items:center}.justify-center{justify-content:center}.justify-between{justify-content:space-between}.overflow-auto{overflow:auto}.rounded{border-radius:.25rem}.bg-white{--tw-bg-opacity: 1;background-color:rgb(255 255 255 / var(--tw-bg-opacity, 1))}.px-\\[3px\\]{padding-left:3px;padding-right:3px}.pb-4{padding-bottom:4rem}.pt-8{padding-top:8rem}.pt-\\[3px\\]{padding-top:3px}.text-coal{color:#1a1a1a}.shadow{--tw-shadow: 0 1px 3px 0 rgb(0 0 0 / .1), 0 1px 2px -1px rgb(0 0 0 / .1);--tw-shadow-colored: 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.outline{outline-style:solid}.filter{filter:var(--tw-blur) var(--tw-brightness) var(--tw-contrast) var(--tw-grayscale) var(--tw-hue-rotate) var(--tw-invert) var(--tw-saturate) var(--tw-sepia) var(--tw-drop-shadow)}.ease-in{transition-timing-function:cubic-bezier(.4,0,1,1)}.ease-out{transition-timing-function:cubic-bezier(0,0,.2,1)}@keyframes backdrop-fade{0%{background:transparent}to{background:var(--nui-backdrop-layer, rgba(0, 0, 0, .5))}}@keyframes backdrop-hide{0%{background:var(--nui-backdrop-layer, rgba(0, 0, 0, .5))}to{background:transparent}}dialog::backdrop{position:fixed;top:0;left:0;width:100%;height:100%}dialog[open]::backdrop{animation:backdrop-fade .2s ease-in forwards}dialog.hide::backdrop{animation:backdrop-hide .2s ease-in forwards}@-moz-document url-prefix(){dialog::backdrop{background:var(--nui-backdrop-layer, rgba(0, 0, 0, .5))}}@media (prefers-reduced-motion: reduce){dialog.hide::backdrop,dialog[open]::backdrop{animation:none 0s;background:var(--nui-backdrop-layer, rgba(0, 0, 0, .5))}}.drawer-wrapper{background-color:var(--nui-snow, rgb(255, 255, 255));width:100%;position:fixed;bottom:0;left:0;right:0;padding:0 18px 18px}.drawer-wrapper--full-screen{height:100vh}@media all and (min-width: 760px){.drawer-wrapper{top:0;left:auto;height:100vh;width:33.3vw;padding:0 42px}}@media all and (min-width: 760px) and (max-width: 1160px){.drawer-wrapper{min-width:360px}}@media all and (min-width: 1920px){.drawer-wrapper{max-width:556px}}#drawer-content.scroll-indicator{--scroll-indicator-bottom: 0}@keyframes scroll-indicator-animate-out{0%{--scroll-indicator-bottom: 0}to{--scroll-indicator-bottom: -40px}}@keyframes scroll-indicator-animate-in{0%{--scroll-indicator-bottom: -40px}to{--scroll-indicator-bottom: 0}}#drawer-content.scroll-indicator:after{transition:bottom .3s ease-out;content:"";position:absolute;bottom:var(--scroll-indicator-bottom);left:0;right:0;height:90px;background-image:linear-gradient(to top,var(--drawer-background-color, #fff) 0%,transparent 100%);pointer-events:none}.concrete-box-shadow{box-shadow:0 1px 0 0 var(--nui-concrete, hsl(0, 8%, 95%))}.hover\\:text-breuninger-red-hover:hover{color:#ba0c2f}.focus\\:outline-none:focus{outline:2px solid transparent;outline-offset:2px}.focus\\:ring-2:focus{--tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);--tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);box-shadow:var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow, 0 0 #0000)}';
function Ee(n, e = !0) {
  return Array.isArray(n) ? n.map((t) => {
    if (e && Array.isArray(t))
      throw new Error(
        "The tuple syntax is not supported for `screens`."
      );
    if (typeof t == "string")
      return {
        name: t.toString(),
        not: !1,
        values: [{ min: t, max: void 0 }]
      };
    let [i, r] = t;
    return i = i.toString(), typeof r == "string" ? {
      name: i,
      not: !1,
      values: [{ min: r, max: void 0 }]
    } : Array.isArray(r) ? {
      name: i,
      not: !1,
      values: r.map((o) => pe(o))
    } : { name: i, not: !1, values: [pe(r)] };
  }) : Ee(Object.entries(n ?? {}), !1);
}
function pe({
  "min-width": n,
  min: e = n,
  max: t,
  raw: i
} = {}) {
  return { min: e, max: t, raw: i };
}
const it = { "2xs_and_up": "320px", "2xs_to_xs": { min: "320px", max: "359px" }, xs_and_up: "360px", xs_to_s: { min: "360px", max: "559px" }, s_and_up: "560px", s_to_m: { min: "560px", max: "759px" }, m_and_up: "760px", m_to_l: { min: "760px", max: "959px" }, l_and_up: "960px", l_to_xl: { min: "960px", max: "1159px" }, xl_and_up: "1160px", xl_to_2xl: { min: "1160px", max: "1359px" }, "2xl_and_up": "1360px", "2xl_to_3xl": { min: "1360px", max: "1659px" }, "3xl_and_up": "1660px", "3xl_to_full-hd": { min: "1660px", max: "1919px" }, "full-hd_and_up": "1920px", "up_to_full-hd": { max: "1919px" }, up_to_3xl: { max: "1659px" }, up_to_2xl: { max: "1359px" }, up_to_xl: { max: "1159px" }, up_to_l: { max: "959px" }, up_to_m: { max: "759px" }, up_to_s: { max: "559px" }, up_to_xs: { max: "359px" }, up_to_2xs: { max: "319px" } }, nt = {
  screens: it
};
function rt(n) {
  return n = Array.isArray(n) ? n : [n], n.map((e) => {
    let t = e.values.map((i) => i.raw !== void 0 ? i.raw : [
      i.min && `min-width: ${i.min}`,
      i.max && `max-width: ${i.max}`
    ].filter(Boolean).join(" and "));
    return e.not ? `not all and ${t}` : t;
  }).join(", ");
}
function K() {
  const n = Ee(nt.screens), e = "m_and_up", t = n.find((i) => i.name === e);
  return t || console.log(`No \`${e}\` screen found.`), rt(t);
}
var ot = Object.defineProperty, $ = (n, e, t, i) => {
  for (var r = void 0, o = n.length - 1, s; o >= 0; o--)
    (s = n[o]) && (r = s(e, t, r) || r);
  return r && ot(e, t, r), r;
};
const st = "drawer:open", at = "drawer:close", ee = class ee extends z {
  constructor() {
    super(...arguments), this.drawerRef = k(), this.drawerWrapperRef = k(), this.drawerHeaderRef = k(), this.drawerBodyRef = k(), this.drawerFooterRef = k(), this.id = "", this.open = !1, this.fullScreen = !1, this.isScrollable = !1, this.handleEscapeKey = (e) => {
      e.key === "Escape" && (e.preventDefault(), this.hide());
    }, this.handleWrapperClick = (e) => {
      e.stopPropagation();
    }, this.updateBodyHeight = () => {
      if (!this.drawerHeaderRef.value || !this.drawerBodyRef.value)
        return;
      const e = this.calculateBodyHeight();
      window.matchMedia(`(${K()})`).matches ? (this.drawerBodyRef.value.style.maxHeight = "", this.drawerBodyRef.value.style.height = e) : (this.drawerBodyRef.value.style.height = "", this.drawerBodyRef.value.style.maxHeight = e);
    };
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), this.handleOutsideClick = this.handleOutsideClick.bind(this), this.updateBodyHeight = this.updateBodyHeight.bind(this), this.createAnimations = this.createAnimations.bind(this), window.addEventListener("resize", this.updateBodyHeight), window.addEventListener("resize", this.createAnimations), (e = this.drawerWrapperRef.value) == null || e.addEventListener(
      "animationend",
      (t) => {
        console.log("animated", t.target);
      }
    );
  }
  disconnectedCallback() {
    var e, t, i;
    window.removeEventListener("resize", this.updateBodyHeight), window.removeEventListener("resize", this.createAnimations), (e = this.drawerRef.value) == null || e.removeEventListener(
      "click",
      this.handleOutsideClick
    ), (t = this.showDrawerAnimation) == null || t.cancel(), (i = this.hideDrawerAnimation) == null || i.cancel(), super.disconnectedCallback();
  }
  updated(e) {
    super.updated(e), this.isScrollable = this.isContentScrollable();
  }
  firstUpdated() {
    this.drawerWrapperRef.value && (this.createAnimations(), this.updateBodyHeight());
  }
  createAnimations() {
    const e = this.drawerWrapperRef.value;
    if (!e)
      return;
    const i = window.matchMedia(`(${K()})`).matches ? "translateX" : "translateY", r = new KeyframeEffect(
      e,
      [
        { transform: `${i}(100%)` },
        { transform: `${i}(0)` }
      ],
      { duration: 300, easing: "ease-out" }
    );
    this.showDrawerAnimation = new Animation(
      r,
      document.timeline
    );
    const o = new KeyframeEffect(
      e,
      [
        { transform: `${i}(0)` },
        { transform: `${i}(100%)` }
      ],
      { duration: 200, easing: "ease-in" }
    );
    this.hideDrawerAnimation = new Animation(
      o,
      document.timeline
    );
  }
  get eventPrefix() {
    return this.id ? `${this.id}-` : "";
  }
  dispatchOpenEvent() {
    const e = new CustomEvent(
      `${this.eventPrefix}${st}`,
      {
        bubbles: !0
      }
    );
    this.dispatchEvent(e);
  }
  dispatchCloseEvent() {
    const e = new CustomEvent(
      `${this.eventPrefix}${at}`,
      {
        bubbles: !0,
        composed: !0
      }
    );
    this.dispatchEvent(e);
  }
  handleOutsideClick(e) {
    this.hide();
  }
  isContentScrollable() {
    return this.scrollContainer ? this.scrollContainer.scrollHeight > this.scrollContainer.clientHeight : !1;
  }
  show() {
    var e, t, i;
    this.open = !0, (e = this.drawerRef.value) == null || e.showModal(), this.refreshBody(), (t = this.drawerRef.value) == null || t.addEventListener("click", this.handleOutsideClick), document.addEventListener("keydown", this.handleEscapeKey), this.prefersReducedMotion() || (i = this.showDrawerAnimation) == null || i.play(), this.dispatchOpenEvent();
  }
  async hide() {
    var e, t, i, r;
    this.prefersReducedMotion() || ((e = this.drawerRef.value) == null || e.classList.add("hide"), (t = this.hideDrawerAnimation) == null || t.play(), await ((i = this.hideDrawerAnimation) == null ? void 0 : i.finished), (r = this.drawerRef.value) == null || r.classList.remove("hide")), document.removeEventListener("keydown", this.handleEscapeKey), this.handleClose();
  }
  /**
   * @description
   * Closes all open drawers on the page.
   * Note: Does not close nested drawers in shadow roots.
   */
  closeAll() {
    document.querySelectorAll(
      "nui-drawer[open]"
    ).forEach((t) => {
      try {
        t.hide();
      } catch (i) {
        console.error("Failed to hide nui-drawer:", t, i);
      }
    });
  }
  handleClose() {
    var e, t;
    this.open = !1, (e = this.drawerRef.value) == null || e.close(), (t = this.drawerRef.value) == null || t.removeEventListener(
      "click",
      this.handleOutsideClick
    ), this.dispatchCloseEvent();
  }
  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  refreshBody() {
    this.isScrollable = this.isContentScrollable(), this.updateBodyHeight();
  }
  handleScroll() {
    if (this.scrollContainer && this.drawerHeader && this.drawerContent) {
      this.scrollContainer.scrollTop > 6 ? this.drawerHeader.classList.add("concrete-box-shadow") : this.drawerHeader.classList.remove("concrete-box-shadow");
      const e = this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight - this.scrollContainer.scrollTop;
      e == 0 ? this.drawerContent.style.setProperty(
        "--scroll-indicator-bottom",
        "-999px"
      ) : e < 40 ? this.drawerContent.style.setProperty(
        "--scroll-indicator-bottom",
        "-40px"
      ) : this.drawerContent.style.setProperty(
        "--scroll-indicator-bottom",
        "0"
      );
    }
  }
  get getHeaderHeight() {
    var e;
    return ((e = this.drawerHeaderRef.value) == null ? void 0 : e.offsetHeight) || 0;
  }
  get getFooterHeight() {
    var e;
    return ((e = this.drawerFooterRef.value) == null ? void 0 : e.offsetHeight) || 0;
  }
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  }
  calculateBodyHeight() {
    const e = window.matchMedia(`(${K()})`).matches, t = this.getFooterHeight ? 18 : 42, i = this.getFooterHeight || 0, r = this.isIOS() ? 90 : 0, o = 90, s = "100vh", l = t + r + o + this.getHeaderHeight + this.getFooterHeight;
    return !e && !this.fullScreen ? `calc(100vh - ${l}px)` : `calc(${s} - ${this.getHeaderHeight}px - ${i}px - ${t}px)`;
  }
  get closeButton() {
    return he`<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M4.00018 2.49993 2.58597 3.91414 20.0859 21.4142 21.5001 20 4.00018 2.49993z" fill="currentColor"/><path d="M2.5 20l1.41421 1.4142L21.5 4.00003 20.0858 2.58582 2.5 20z" fill="currentColor"/></svg>`;
  }
  render() {
    return he`<dialog id="drawer" ${S(this.drawerRef)} aria-labelledby="drawer-title" aria-describedby="drawer-description" role="dialog" aria-modal="true" ?open="${this.open}" data-testid="${this.id}-drawer"><div ${S(this.drawerWrapperRef)} class="${ue({
      "drawer-wrapper": !0,
      "drawer-wrapper--full-screen": this.fullScreen
    })}" @click="${this.handleWrapperClick}"><div id="drawer-header" ${S(this.drawerHeaderRef)} class="pt-8 pb-4 w-full"><div><div class="nui-tw-heading-medium text-coal" id="drawer-title"><slot name="title"></slot></div><div id="drawer-description" class="sr-only"><slot name="description"></slot></div><div class="absolute w-full top-0 left-0 right-0 px-[3px] pt-[3px] bg-white"><div class="flex flex-row justify-between"><div><slot name="header-start"></slot></div><div><slot name="header-center"></slot></div><button @click="${this.hide}" type="button" data-testid="nui-drawer-close-button" class="bg-white rounded focus:outline-none focus:ring-2 w-[48px] h-[48px] flex flex-nowrap justify-center items-center hover:text-breuninger-red-hover"><span class="sr-only">Close</span> ${this.closeButton}</button></div></div></div></div><div id="drawer-content" class="w-full relative mb-1 ${ue({
      "scroll-indicator": this.isScrollable
    })}"><div><div id="scroll-container" class="overflow-auto" ${S(this.drawerBodyRef)} @scroll="${this.handleScroll}"><slot></slot></div></div></div><div class="relative w-full z-10" ${S(this.drawerFooterRef)}><slot name="footer"></slot></div></div></dialog>`;
  }
};
ee.styles = ke`${ge(tt)}`;
let f = ee;
$([
  I({ type: String })
], f.prototype, "id");
$([
  I({ type: Boolean, reflect: !0 })
], f.prototype, "open");
$([
  I({ type: Boolean, reflect: !0 })
], f.prototype, "fullScreen");
$([
  G("#scroll-container")
], f.prototype, "scrollContainer");
$([
  G("#drawer-content")
], f.prototype, "drawerContent");
$([
  G("#drawer-header")
], f.prototype, "drawerHeader");
$([
  qe()
], f.prototype, "isScrollable");
export {
  f as Drawer
};
